/**
 * Firebase Analytics utility for web user platform
 * Tracks screen views, events, and user properties
 * Excludes SUPERADMIN actions from analytics
 */

import { getAnalytics, logEvent, setUserProperties, Analytics } from 'firebase/analytics';
import app from '../services/firebase';

let analytics: Analytics | null = null;

/**
 * Initialize Firebase Analytics
 * Should be called once when the app loads
 */
export const initFirebaseAnalytics = (): void => {
  try {
    // Check if analytics is already initialized
    if (analytics) {
      return;
    }

    // Initialize Analytics
    analytics = getAnalytics(app);
  } catch (error) {
    // Analytics might not be available in development or if not configured
    console.warn('Firebase Analytics initialization failed:', error);
    analytics = null;
  }
};

/**
 * Set user properties (including role for filtering)
 * Should be called after user authentication
 */
export const setAnalyticsUserProperties = async (userId: string, userRole: string): Promise<void> => {
  if (!analytics) {
    return;
  }

  try {
    // Set user properties for filtering
    // Exclude SUPERADMIN from analytics
    await setUserProperties(analytics, {
      user_id: userId,
      user_role: userRole,
      // This will be used to filter out SUPERADMIN in Firebase Console
    });
  } catch (error) {
    console.warn('Failed to set Firebase Analytics user properties:', error);
  }
};

/**
 * Track a screen view
 * Excludes SUPERADMIN actions
 */
export const logScreenView = (screenName: string, userRole?: string): void => {
  // Skip analytics for SUPERADMIN
  if (userRole === 'SUPERADMIN') {
    return;
  }

  if (!analytics) {
    return;
  }

  try {
    logEvent(analytics, 'screen_view' as any, {
      screen_name: screenName,
      screen_class: screenName,
    });
  } catch (error) {
    // Silently fail - analytics errors shouldn't break the app
  }
};

/**
 * Track a custom event
 * Excludes SUPERADMIN actions
 */
export const logAnalyticsEvent = (
  eventName: string,
  eventParams?: Record<string, string | number | boolean>,
  userRole?: string
): void => {
  // Skip analytics for SUPERADMIN
  if (userRole === 'SUPERADMIN') {
    return;
  }

  if (!analytics) {
    return;
  }

  try {
    logEvent(analytics, eventName as any, eventParams);
  } catch (error) {
    // Silently fail - analytics errors shouldn't break the app
  }
};

/**
 * Track feature usage events
 */
export const logFeatureUsage = (
  featureName: string,
  action: string,
  userRole?: string
): void => {
  logAnalyticsEvent('feature_usage', {
    feature_name: featureName,
    action,
  }, userRole);
};

/**
 * Track user engagement events
 */
export const logEngagement = (
  engagementType: string,
  value?: number,
  userRole?: string
): void => {
  logAnalyticsEvent('user_engagement', {
    engagement_type: engagementType,
    ...(value !== undefined && { value }),
  }, userRole);
};

// Export analytics instance for direct access if needed
export { analytics };

